/**
 * Custom hook for NCR Edit state management
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  qualityAPI,
  coreAPI,
  NonConformanceReport,
  NCRDocument,
  NCRComment,
  NCRActionLog,
  UserProfile,
  Warehouse,
} from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import { displayPrice } from '../../lib/unitPrice';
import { NCREditFormData } from './types';
import type { CloseDialogMode } from './Sections';
import { customerOwns8D } from './ownership';
import { useNCRPlatformOptions } from './useNCRPlatformOptions';
import { useNCRSubmitHandlers } from './useNCRSubmitHandlers';
import { useNCRReviewHandlers } from './useNCRReviewHandlers';
import { useNCRWorkflowHandlers } from './useNCRWorkflowHandlers';

export function useNCREdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // CR-4707 precision, same source the create form and PO Line pop-up read.
  const { preferences } = useFormatters();
  const priceDecimals = preferences.unit_price_decimals;

  // Data states
  const [ncr, setNcr] = useState<NonConformanceReport | null>(null);
  const [documents, setDocuments] = useState<NCRDocument[]>([]);
  const [comments, setComments] = useState<NCRComment[]>([]);
  const [actionLogs, setActionLogs] = useState<NCRActionLog[]>([]);

  // UI states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [customerUsers, setCustomerUsers] = useState<UserProfile[]>([]);
  // CR-7347: options for the Warehouse picker in Order Information. Customer
  // side only — a supplier never sets the receiving warehouse.
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);

  // Classification dropdown options
  const { sourcesOfRejection, commonisedActions, faultGroups, faultCategories } = useNCRPlatformOptions();

  // Form states
  const [formData, setFormData] = useState<NCREditFormData>({});
  const [newComment, setNewComment] = useState('');
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  // What the confirm dialog does: approve the 8D only (other processes still
  // open), approve it and close (it was the last thing open), or close.
  const [closeDialogMode, setCloseDialogMode] = useState<CloseDialogMode>('approve_and_close');
  // CR-6024: customer-side CAR rejection modal (Reject & Rework path).
  const [rejectCARDialogOpen, setRejectCARDialogOpen] = useState(false);
  // CR-6024 r3: customer-side reopen-closed-NCR modal.
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  // Supplier rejects the whole NCR with a free-text reason; customer then
  // approves (voids the NCR) or declines. The audit log + void happen backend-side.
  const [rejectNCRDialogOpen, setRejectNCRDialogOpen] = useState(false);
  // 8D scoring: RC/CA scores (1-5) the customer grades the submitted CAR
  // with before clicking Approve / Reject & Rework. Sent with the decision.
  // CR-7357: four grades — ORC (rc), ERC, OPA (ca), EPA.
  const [rcScore, setRcScore] = useState<number | null>(null);
  const [ercScore, setErcScore] = useState<number | null>(null);
  const [caScore, setCaScore] = useState<number | null>(null);
  const [epaScore, setEpaScore] = useState<number | null>(null);
  // CR-5999: per-field validation errors surfaced inline next to each
  // required RCA field on Submit RCA. Keys are field names from
  // RCA_REQUIRED_FIELDS; presence of a key = the field failed validation.
  const [rcaFieldErrors, setRcaFieldErrors] = useState<Record<string, string>>({});
  // CR-6000: per-field validation errors surfaced inline next to each required
  // CAR field on Submit CAR. Keys are field names from CAR_REQUIRED_FIELDS;
  // presence of a key = the field failed validation. Mirrors rcaFieldErrors.
  const [carFieldErrors, setCarFieldErrors] = useState<Record<string, string>>({});

  // Template 2 configuration - prefer formData (live edits) over saved ncr data
  const is8DRequired = (formData.is_8d_required as boolean | undefined) ?? ncr?.is_8d_required ?? true;
  const isCollectionRequired = (formData.is_collection_required as boolean | undefined) ?? ncr?.is_collection_required ?? false;
  const isCreditRequired = (formData.is_credit_required as boolean | undefined) ?? ncr?.is_credit_required ?? false;
  const liabilityType = (formData.liability_type as number | undefined) ?? ncr?.liability_type ?? 0;

  // Get user role from auth context
  const { user } = useAuth();
  const userRole = user?.company_type || user?.platform?.toLowerCase() || 'customer';
  const isCustomer = userRole === 'customer' || userRole === 'helix';
  const isSupplier = userRole === 'supplier';
  // CR-6024 r4: separate gate for write actions that require ACTUAL customer
  // affiliation. Helix tenants cannot directly fire customer/supplier
  // workflow transitions (the backend's user_can_act_for_company check
  // rejects them); they have to use Login As first. Surfacing the Approve /
  // Reject / Reopen buttons to a Helix admin produces a confusing "Only the
  // company that raised the NCR can reject the CAR" error toast on click.
  // Keep `isCustomer` permissive for read-only UI (banners, panels), but
  // gate the action affordances on this stricter flag.
  const canActAsCustomer = userRole === 'customer';

  // Workflow permissions. CR-5980: supplier edits the RCA tab when the NCR
  // is in the 'created' state (customer has submitted, supplier hasn't yet),
  // and edits the CAR tab once at 'ca_submitted'.
  // CR-8707: once submitted, the details change only under an admin's Master
  // Edit (NCREdit's masterEditActive) -- the server holds the same rule.
  const canEditStep1 = isCustomer && ncr?.status === 'draft';
  // When the customer returns the CAR for rework the NCR drops to
  // 'ca_submitted' with a car_rejection_reason set. In that state the supplier
  // must be able to amend the RCA too — not just the CAR — so re-enable RCA
  // editing on a rework, in addition to the initial 'created' submission.
  // For an internal NCR the customer's assigned team owns the 8D; otherwise
  // the supplier does. The backend enforces the same in submit_rca/submit_car.
  // Spec A5 (24 Sep 2026): the customer also owns the 8D of a customer-
  // liability NCR, as for an internal one.
  const isInternalNcr = customerOwns8D(ncr);
  const owns8D = isInternalNcr ? isCustomer : isSupplier;
  const carReturnedForRework =
    ncr?.status === 'ca_submitted' && !!(ncr?.car_rejection_reason || '').trim();
  const canEditStep2 = owns8D && (ncr?.status === 'created' || carReturnedForRework);
  const canEditStep3 = owns8D && ncr?.status === 'ca_submitted';
  // The supplier may INPUT the Corrective Action Report alongside the RCA
  // (status 'created'), not only after the RCA is submitted. The Save button
  // persists it as a draft; submitting the CAR still requires 'ca_submitted'
  // (canEditStep3), so the RCA must be submitted first.
  const canInputCAR = owns8D && (ncr?.status === 'created' || ncr?.status === 'ca_submitted');

  // Fetch NCR details
  useEffect(() => {
    if (id) {
      fetchNCRDetails();
    }
  }, [id]);

  // CR-6001: ``isRefresh`` distinguishes the initial mount fetch (which
  // toggles ``loading`` to show the full-screen spinner) from post-save /
  // post-action refetches (which keep ``loading`` false so the Tabs tree
  // stays mounted). Without this split, every save would unmount the
  // inner Root Cause / Corrective Action Tabs, and the inner Tabs'
  // ``defaultValue`` (status-driven) would snap the user back to Root
  // Cause — which is exactly the "page refreshes back to RCA" symptom
  // CR-6001 reported.
  const fetchNCRDetails = async (isRefresh = false) => {
    if (!id) return;

    try {
      if (!isRefresh) setLoading(true);
      const [ncrResponse, docsResponse, commentsResponse, logsResponse] = await Promise.all([
        qualityAPI.getNCR(parseInt(id)),
        qualityAPI.getNCRDocuments(parseInt(id)),
        qualityAPI.getNCRComments(parseInt(id)),
        qualityAPI.getNCRActionLogs(parseInt(id)),
      ]);

      const ncrData = ncrResponse.data;
      setNcr(ncrData);
      // The column holds 4 decimals so the Regional Settings precision can go
      // that high, but the API returns the stored scale verbatim — "10.0000"
      // for a price entered as 10. `displayPrice` trims that back to the
      // configured default while keeping any extra decimal the user actually
      // entered, so re-saving an untouched NCR cannot quietly round its price.
      // Done once on load, not per render, which would rewrite the box while
      // it is being typed into.
      setFormData({
        ...(ncrData as NCREditFormData),
        unit_price: ncrData.unit_price != null && ncrData.unit_price !== ''
          ? displayPrice(ncrData.unit_price, priceDecimals)
          : '',
      });
      // 8D scoring: seed the review dropdowns with any previously-saved
      // grades (e.g. scores captured on an earlier rework round).
      setRcScore(ncrData.rc_score ?? null);
      setErcScore(ncrData.erc_score ?? null);
      setCaScore(ncrData.ca_score ?? null);
      setEpaScore(ncrData.epa_score ?? null);
      setDocuments(docsResponse.data.results);
      setComments(commentsResponse.data.results);
      setActionLogs(logsResponse.data.results);

      // Fetch customer site users for the buyer dropdown. Customer-side
      // only: the endpoint is scoped to the customer's own company and 403s
      // for a supplier, who cannot edit the buyer anyway — calling it
      // logged a console error on every supplier load and action.
      if (ncrData.company && !isSupplier) {
        try {
          const usersResponse = await coreAPI.getCompanyUsers(ncrData.company);
          setCustomerUsers(usersResponse.data);
        } catch {
          console.error('Error fetching customer users');
        }
        // A failure here leaves the Warehouse field as read-only text rather
        // than blocking the NCR.
        try {
          const warehouseResponse = await coreAPI.getAccessibleWarehouses();
          setWarehouses(Array.isArray(warehouseResponse.data) ? warehouseResponse.data : []);
        } catch {
          console.error('Error fetching warehouses');
        }
      }

      // Determine current step based on status. CR-5980: 'draft' and
      // 'created' are both pre-supplier-action so they map to step 1; the
      // stepper only advances once the supplier submits CA or CAR.
      if (ncrData.status === 'draft') setCurrentStep(1);
      else if (ncrData.status === 'created') setCurrentStep(1);
      else if (ncrData.status === 'ca_submitted') setCurrentStep(2);
      else if (ncrData.status === 'car_submitted') setCurrentStep(3);
      else if (ncrData.status === 'eightd_complete') setCurrentStep(4);
      else if (ncrData.status === 'submitted') setCurrentStep(1);
      else if (ncrData.status === 'closed') setCurrentStep(4);
      return ncrData;
    } catch (error) {
      console.error('Error fetching NCR details:', error);
    } finally {
      if (!isRefresh) setLoading(false);
    }
  };

  /** E21: after the customer's own action, if that was the last thing
   *  outstanding, ask whether to close the NCR now (they may keep it open). */
  const offerCloseIfLast = (fresh: NonConformanceReport | undefined) => {
    if (!fresh || isSupplier || fresh.ncr_state !== 'Open') return;
    if ((fresh.outstanding_processes ?? []).length > 0) return;
    setCloseDialogMode('last_action');
    setCloseDialogOpen(true);
  };

  /** Open the confirm dialog in the right mode for what is still open. */
  const openCloseDialog = (intent: 'approve' | 'close') => {
    const others = (ncr?.outstanding_processes ?? []).filter((p) => p !== '8D');
    setCloseDialogMode(intent === 'close' ? 'close' : others.length ? 'approve_only' : 'approve_and_close');
    setCloseDialogOpen(true);
  };

  const { handleSave, handleSubmitDetail, handleSubmitRCA, handleSubmitCAR } = useNCRSubmitHandlers({
    id,
    formData,
    setSaving,
    setRcaFieldErrors,
    setCarFieldErrors,
    fetchNCRDetails,
  });

  const {
    handleAddComment,
    handleClose,
    handleApprove8D,
    handleRejectCAR,
    handleRcScoreChange,
    handleErcScoreChange,
    handleCaScoreChange,
    handleEpaScoreChange,
    handleReopen,
    handleRejectNCR,
    handleApproveRejection,
    handleDeclineRejection,
  } = useNCRReviewHandlers({
    id,
    ncr,
    formData,
    newComment,
    setNewComment,
    navigate,
    fetchNCRDetails,
    setCloseDialogOpen,
    setRejectCARDialogOpen,
    setReopenDialogOpen,
    setRejectNCRDialogOpen,
    rcScore,
    ercScore,
    caScore,
    epaScore,
    setRcScore,
    setErcScore,
    setCaScore,
    setEpaScore,
  });

  const {
    handleConfirmCollection,
    handleConfirmCredit,
    handleInitiateCollection,
    handleUpdateCollectionStatus,
    handleAssignCollection,
    handleInitiateCredit,
    handleSubmitCreditNote,
    handleUpdateCreditStatus,
    handleInitiateReplacement,
    handleSubmitReplacementDispatch,
    handleUpdateReplacementStatus,
  } = useNCRWorkflowHandlers({ id, setSaving, fetchNCRDetails, offerCloseIfLast });

  // DE review (Vaida, 2026-07): the 8D is its own workflow and must be
  // approvable irrespective of the collection / credit / replacement claim
  // workflows, which run independently. Approval therefore gates only on the
  // CAR having been submitted, not on those commercial steps completing.
  // The CAR can be approved whenever it is submitted; closing waits for every
  // required process (server-computed outstanding_processes).
  const canClose = !!ncr && ncr.status === 'car_submitted';

  const updateFormData = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // CR-5999: clear the inline RCA error for this field as soon as the
    // user starts typing — the error has been addressed.
    if (rcaFieldErrors[field]) {
      setRcaFieldErrors((prev) => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
    // CR-6000: same self-clearing behaviour for the CAR field errors.
    if (carFieldErrors[field]) {
      setCarFieldErrors((prev) => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const goBack = () => navigate('/quality');

  return {
    // Data
    ncr,
    documents,
    comments,
    actionLogs,
    formData,

    // UI states
    loading,
    saving,
    currentStep,
    newComment,
    closeDialogOpen,
    closeDialogMode,
    openCloseDialog,
    handleApprove8D,

    // Permissions
    isCustomer,
    canActAsCustomer,
    isSupplier,
    canEditStep1,
    canEditStep2,
    canEditStep3,
    canInputCAR,

    // CR-5999: per-field RCA validation errors (e.g. {'why_1': 'This field
    // is required'}) that the RCA tab renders inline next to each empty
    // required field on a failed Submit RCA attempt.
    rcaFieldErrors,

    // CR-6000: per-field CAR validation errors (e.g. {'preventive_action':
    // 'This field is required'}) rendered inline on a failed Submit CAR.
    carFieldErrors,

    // Setters
    setNewComment,
    setCloseDialogOpen,
    // CR-6024
    rejectCARDialogOpen,
    setRejectCARDialogOpen,
    handleRejectCAR,
    // CR-6024 r3: reopen-closed-NCR modal
    reopenDialogOpen,
    setReopenDialogOpen,
    handleReopen,
    // Supplier reject-NCR + customer approve/decline
    rejectNCRDialogOpen,
    setRejectNCRDialogOpen,
    handleRejectNCR,
    handleApproveRejection,
    handleDeclineRejection,
    // 8D scoring: customer CAR grades captured with the approve/rework decision
    // CR-7357: four dimensions — ORC (rc), ERC, OPA (ca), EPA.
    rcScore,
    ercScore,
    caScore,
    epaScore,
    setRcScore,
    setErcScore,
    setCaScore,
    setEpaScore,
    handleRcScoreChange,
    handleErcScoreChange,
    handleCaScoreChange,
    handleEpaScoreChange,
    updateFormData,

    // Customer users for buyer dropdown
    customerUsers,
    warehouses,

    // Classification options
    sourcesOfRejection,
    commonisedActions,
    faultGroups,
    faultCategories,

    // Template 2 config
    liabilityType,
    is8DRequired,
    isCollectionRequired,
    isCreditRequired,

    // Actions
    // CR-6001: external refreshes (e.g. after document upload) go through
    // refresh mode so the Tabs tree doesn't unmount and snap the user back
    // to the Root Cause sub-tab.
    refreshData: () => fetchNCRDetails(true),
    goBack,
    handleSave,
    handleSubmitDetail,
    handleSubmitRCA,
    handleSubmitCAR,
    handleAddComment,
    handleClose,
    handleConfirmCollection,
    handleConfirmCredit,

    // Multi-step workflow actions
    handleInitiateCollection,
    handleUpdateCollectionStatus,
    handleAssignCollection,
    handleInitiateCredit,
    handleSubmitCreditNote,
    handleUpdateCreditStatus,
    handleInitiateReplacement,
    handleSubmitReplacementDispatch,
    handleUpdateReplacementStatus,

    // Computed
    canClose,
  };
}
